import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WarehouseProvider } from './context/WarehouseContext';
import { ClientProvider } from './context/ClientContext';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import AppShell from './components/layout/AppShell';

import Login from './pages/auth/Login';
import ChangePassword from './pages/auth/ChangePassword';
import Dashboard from './pages/dashboard/Dashboard';
import InwardList from './pages/inward/InwardList';
import InwardForm from './pages/inward/InwardForm';
import InwardDetail from './pages/inward/InwardDetail';
import QuarantineList from './pages/quarantine/QuarantineList';
import GRNList from './pages/grn/GRNList';
import GRNForm from './pages/grn/GRNForm';
import GRNDetail from './pages/grn/GRNDetail';
import RackView from './pages/rack/RackView';
import DispatchList from './pages/dispatch/DispatchList';
import DispatchForm from './pages/dispatch/DispatchForm';
import DispatchDetail from './pages/dispatch/DispatchDetail';
import DispatchHoldList from './pages/dispatch-hold/DispatchHoldList';
import DirectDeliveryList from './pages/delivery/DirectDeliveryList';
import DirectDeliveryForm from './pages/delivery/DirectDeliveryForm';
import DirectDeliveryDetail from './pages/delivery/DirectDeliveryDetail';
import UserManagement from './pages/users/UserManagement';
import WarehouseList from './pages/warehouses/WarehouseList';
import ClientList from './pages/clients/ClientList';
import ProductList from './pages/products/ProductList';
import SupplierList from './pages/suppliers/SupplierList';
import ClientInventory from './pages/portal/ClientInventory';
import ClientInbound from './pages/portal/ClientInbound';
import ClientDispatches from './pages/portal/ClientDispatches';
import RolesPermissions from './pages/admin/RolesPermissions';
import ModulesFeatures from './pages/admin/ModulesFeatures';
import StorageSetup from './pages/storage/StorageSetup';
import ReportsAnalytics from './pages/reports/ReportsAnalytics';

const Shell = ({ children }) => <AppShell>{children}</AppShell>;

const landingFor = (role) => ({
  super_admin: '/dashboard',
  admin: '/dashboard',
  store_executive: '/inward',
  dispatch_executive: '/dispatch',
  gate_security: '/dashboard',
  client_user: '/portal',
}[role] || '/dashboard');

const AppRoutes = () => {
  const { user } = useAuth();

  // Global guard: a user who must change their password is locked to that screen
  // for every other route until the password change succeeds.
  if (user && user.mustChangePassword) {
    return (
      <Routes>
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="*" element={<Navigate to="/change-password" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/change-password" element={
        <ProtectedRoute>
          <ChangePassword />
        </ProtectedRoute>
      } />
      <Route
        path="/unauthorized"
        element={
          <div className="min-h-screen flex items-center justify-center text-zip-muted">
            403 — Not Authorized
          </div>
        }
      />

      <Route path="/dashboard" element={
        <ProtectedRoute permission="dashboard.view" module="dashboard">
          <Shell><Dashboard /></Shell>
        </ProtectedRoute>
      } />

      <Route path="/inward" element={
        <ProtectedRoute permission="inward.view" module="inward">
          <Shell><InwardList /></Shell>
        </ProtectedRoute>
      } />
      <Route path="/inward/new" element={
        <ProtectedRoute permission="inward.create" module="inward">
          <Shell><InwardForm /></Shell>
        </ProtectedRoute>
      } />
      <Route path="/inward/:id" element={
        <ProtectedRoute permission="inward.view" module="inward">
          <Shell><InwardDetail /></Shell>
        </ProtectedRoute>
      } />

      <Route path="/quarantine" element={
        <ProtectedRoute permission="quarantine.view" module="quarantine">
          <Shell><QuarantineList /></Shell>
        </ProtectedRoute>
      } />

      <Route path="/grn" element={
        <ProtectedRoute permission="grn.view" module="grn">
          <Shell><GRNList /></Shell>
        </ProtectedRoute>
      } />
      <Route path="/grn/new" element={
        <ProtectedRoute permission="grn.create" module="grn">
          <Shell><GRNForm /></Shell>
        </ProtectedRoute>
      } />
      <Route path="/grn/:id" element={
        <ProtectedRoute permission="grn.view" module="grn">
          <Shell><GRNDetail /></Shell>
        </ProtectedRoute>
      } />

      <Route path="/rack" element={
        <ProtectedRoute permission="rack.view" module="rack">
          <Shell><RackView /></Shell>
        </ProtectedRoute>
      } />

      <Route path="/storage" element={
        <ProtectedRoute permission="storage.view" module="storage">
          <Shell><StorageSetup /></Shell>
        </ProtectedRoute>
      } />

      <Route path="/dispatch" element={
        <ProtectedRoute permission="dispatch.view" module="dispatch">
          <Shell><DispatchList /></Shell>
        </ProtectedRoute>
      } />
      <Route path="/dispatch/new" element={
        <ProtectedRoute permission="dispatch.create" module="dispatch">
          <Shell><DispatchForm /></Shell>
        </ProtectedRoute>
      } />
      <Route path="/dispatch/:id" element={
        <ProtectedRoute permission="dispatch.view" module="dispatch">
          <Shell><DispatchDetail /></Shell>
        </ProtectedRoute>
      } />

      <Route path="/dispatch-hold" element={
        <ProtectedRoute permission="dispatch_hold.view" module="dispatch_hold">
          <Shell><DispatchHoldList /></Shell>
        </ProtectedRoute>
      } />

      <Route path="/direct-delivery" element={
        <ProtectedRoute permission="direct_delivery.view" module="direct_delivery">
          <Shell><DirectDeliveryList /></Shell>
        </ProtectedRoute>
      } />
      <Route path="/direct-delivery/new" element={
        <ProtectedRoute permission="direct_delivery.create" module="direct_delivery">
          <Shell><DirectDeliveryForm /></Shell>
        </ProtectedRoute>
      } />
      <Route path="/direct-delivery/:id" element={
        <ProtectedRoute permission="direct_delivery.view" module="direct_delivery">
          <Shell><DirectDeliveryDetail /></Shell>
        </ProtectedRoute>
      } />

      <Route path="/warehouses" element={
        <ProtectedRoute permission="warehouses.view" module="warehouses">
          <Shell><WarehouseList /></Shell>
        </ProtectedRoute>
      } />

      <Route path="/clients" element={
        <ProtectedRoute permission="clients.view" module="clients">
          <Shell><ClientList /></Shell>
        </ProtectedRoute>
      } />

      <Route path="/reports" element={
        <ProtectedRoute permission="reports.view" module="reports">
          <Shell><ReportsAnalytics /></Shell>
        </ProtectedRoute>
      } />

      <Route path="/products" element={
        <ProtectedRoute permission="products.view" module="products">
          <Shell><ProductList /></Shell>
        </ProtectedRoute>
      } />

      <Route path="/suppliers" element={
        <ProtectedRoute permission="suppliers.view" module="suppliers">
          <Shell><SupplierList /></Shell>
        </ProtectedRoute>
      } />

      <Route path="/users" element={
        <ProtectedRoute permission="users.view" module="users">
          <Shell><UserManagement /></Shell>
        </ProtectedRoute>
      } />

      {/* Super Admin panel */}
      <Route path="/admin/roles" element={
        <ProtectedRoute permission="settings.manage">
          <Shell><RolesPermissions /></Shell>
        </ProtectedRoute>
      } />
      <Route path="/admin/modules" element={
        <ProtectedRoute permission="settings.manage">
          <Shell><ModulesFeatures /></Shell>
        </ProtectedRoute>
      } />

      {/* Client portal (read-only, scoped server-side to the user's own client) */}
      <Route path="/portal" element={
        <ProtectedRoute roles={['client_user']}>
          <Shell><ClientInventory /></Shell>
        </ProtectedRoute>
      } />
      <Route path="/portal/inbound" element={
        <ProtectedRoute roles={['client_user']}>
          <Shell><ClientInbound /></Shell>
        </ProtectedRoute>
      } />
      <Route path="/portal/dispatches" element={
        <ProtectedRoute roles={['client_user']}>
          <Shell><ClientDispatches /></Shell>
        </ProtectedRoute>
      } />

      <Route path="/" element={<Navigate to={landingFor(user?.role)} replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
      <WarehouseProvider>
        <ClientProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 3000,
                style: {
                  background: '#FFFFFF',
                  color: '#0F172A',
                  border: '1px solid #E2E8F0',
                  borderRadius: '10px',
                  fontSize: '13px',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                },
              }}
            />
          </BrowserRouter>
        </ClientProvider>
      </WarehouseProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
